/**
 * Boolean that stays true for at least `minMs` after the source signal
 * turns true. Useful for keeping a loader/pill visible long enough that
 * users perceive it, even when the underlying network fetch resolves in
 * a few hundred ms (cache hits, small payloads).
 *
 *   const showPill = useMinVisible(loading, 500);
 */
import { useEffect, useRef, useState } from "react";

export default function useMinVisible(active, minMs = 500) {
  const [visible, setVisible] = useState(active);
  const startedAt = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (active) {
      startedAt.current = Date.now();
      setVisible(true);
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    } else if (startedAt.current !== null) {
      const elapsed = Date.now() - startedAt.current;
      const remaining = Math.max(0, minMs - elapsed);
      if (remaining === 0) {
        setVisible(false);
        startedAt.current = null;
      } else {
        timerRef.current = setTimeout(() => {
          setVisible(false);
          startedAt.current = null;
          timerRef.current = null;
        }, remaining);
      }
    }
    return () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [active, minMs]);

  return visible;
}
